import { Dialog, Heading, Modal, ModalOverlay } from "react-aria-components";
import { Trash2 } from "lucide-react";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { cn } from "@/utils/helpers";

/*
 * References / Confirm · Delete (library, style B): a solid dialog, radius 16,
 * 560 wide. The question in Barlow Condensed capitals, what goes with it, THIS
 * CANNOT BE UNDONE in the alert ink, then Cancel (ghost) and Delete (danger)
 * over a hairline.
 */
export default function ConfirmDelete({
  isOpen,
  title,
  description,
  confirmLabel = "Delete",
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const portal = useCoShellPortal();
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onCancel()}
      isDismissable
      UNSTABLE_portalContainer={portal ?? undefined}
      className={({ isEntering }) =>
        cn(
          "fixed inset-0 z-[calc(var(--z-index-modal)+1)] flex items-center justify-center bg-co-scrim p-4",
          isEntering && "animate-in-smooth fade-in",
        )
      }
    >
      <Modal className={({ isEntering }) => cn("w-full max-w-[560px] outline-none", isEntering && "animate-in-smooth dialog-in")}>
        <Dialog role="alertdialog" className="ref-confirm outline-none">
          <div className="flex flex-col gap-2 px-6 pt-6">
            <Heading slot="title" className="ref-confirm-title">
              {title}
            </Heading>
            <p className="text-[13.5px] leading-[1.55] text-co-muted">{description}</p>
          </div>
          <p className="ref-mono px-6 py-[18px] text-co-alert-ink">This cannot be undone</p>
          <div className="flex justify-end gap-2 border-t border-co-line px-6 py-4">
            <button type="button" autoFocus className="ref-btn ref-btn--ghost h-9" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="ref-btn ref-btn--danger h-9" onClick={onConfirm}>
              <Trash2 aria-hidden className="size-3.5" />
              {confirmLabel}
            </button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
